// deps
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

// internal-deps
import { replaceText } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { isEmpty } from "@swiggy-private/js-utils";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

// widgets
import { DeliverySpeedPointTooltipTitle, DeliverySpeedPoint, Wrapper } from "./DeliverySpeedPointContainer.style";
import DeliverySpeedPointTooltip from "@cart-ui/components/delivery-speed/components/delivery-speed-point-tooltip";

// styles
import styles from "../../styles/DeliverySpeed.scss";

// helpers
import { getDeliverySpeedTooltipTestId } from "@cart-ui/helpers/DeliverySpeed";
import { META_TYPES_V4 } from "@cart-ui/constants/BillDetails";

interface DeliverySpeedPointContainerProps {
    index?: number;
    shimmer?: boolean;
    text: string;
    tooltipData?: {
        title?: string;
        description?: string;
    };
    highlightColor?: string;
}

function DeliverySpeedPointContainer(props: DeliverySpeedPointContainerProps) {
    const { index = 0, shimmer, text, tooltipData, highlightColor } = props;

    const bulletPointRef = useRef<HTMLDivElement>(null);
    const [showBulletPointShimmer, setShowBulletPointShimmer] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    const isTooltipDataAvailable = useMemo(() => {
        return !isEmpty(tooltipData?.title) && !isEmpty(tooltipData?.description);
    }, [tooltipData]);

    const handleShowTooltip = useCallback(
        (event: React.MouseEvent) => {
            if (bulletPointRef?.current && isTooltipDataAvailable) {
                const { x, y, left, top, width } = bulletPointRef.current.getBoundingClientRect();
                setTooltipPosition({ x: (x || left) + width / 2, y: y || top }); // Not all browsers gives x, y so we use left and top values in such cases
                setShowTooltip(true);
                event.stopPropagation(); // To prevent card selection on tooltip click
            }
        },
        [isTooltipDataAvailable],
    );

    const onVisibilityChange = useCallback(
        (isVisible: boolean) => {
            if (isVisible) {
                setShowBulletPointShimmer(true);
            }
        },
        [setShowBulletPointShimmer],
    );

    const onHide = useCallback(() => setShowTooltip(false), [setShowTooltip]);

    return (
        <ElementVisibilitySensor senseOnce onVisibilityChange={onVisibilityChange}>
            <Wrapper
                ref={bulletPointRef}
                data-testid={getDeliverySpeedTooltipTestId(index)}
                onClick={handleShowTooltip}
            >
                <DeliverySpeedPoint
                    variant="V2.body2.neutral.medium"
                    shimmer={shimmer && showBulletPointShimmer}
                    showTooltipBorder={isTooltipDataAvailable}
                    highlightColor={highlightColor as keyof IColors}
                    maxLines={1}
                >
                    {replaceText(text, styles.logo)}
                </DeliverySpeedPoint>
            </Wrapper>
            {showTooltip && (
                <DeliverySpeedPointTooltip
                    triggerRef={bulletPointRef}
                    position={tooltipPosition}
                    widgetSize={META_TYPES_V4.WINDOW_WIDTH.XXL}
                    onHide={onHide}
                >
                    <DeliverySpeedPointTooltipTitle
                        variant="V2.body1.neutral.bold"
                        color="text_Highest_Emphasis"
                        data-testid={getDeliverySpeedTooltipTestId(index, "TITLE")}
                    >
                        {tooltipData?.title}
                    </DeliverySpeedPointTooltipTitle>
                    <Text
                        variant="V2.body2.neutral.regular"
                        color="text_High_Emphasis"
                        data-testid={getDeliverySpeedTooltipTestId(index, "DESCRIPTION")}
                    >
                        {tooltipData?.description}
                    </Text>
                </DeliverySpeedPointTooltip>
            )}
        </ElementVisibilitySensor>
    );
}

export default memo(DeliverySpeedPointContainer);
